
import jsPDF from 'jspdf'
import 'jspdf-autotable'

declare module 'jspdf' {
  interface jsPDF {
    autoTable: (options: any) => jsPDF
  }
}

interface DREData {
  periodo: string
  receitas: {
    operacionais: number
    financeiras: number
    outras: number
    total: number
  }
  custos: {
    materiais: number
    maoObra: number
    equipamentos: number
    total: number
  }
  despesas: {
    administrativas: number
    comerciais: number
    financeiras: number
    tributarias: number
    total: number
  }
  resultado: {
    bruto: number
    operacional: number
    liquido: number
    margem: number
  }
}

interface MargemObraData {
  obra_id: string
  nome_obra: string
  cliente: string
  valor_contrato: number
  custos_realizados: number
  receitas_realizadas: number
  margem_bruta: number
  margem_liquida: number
  percentual_execucao: number
  status: string
}

interface PerformanceData {
  mes: string
  receitas: number
  despesas: number
  margem: number
  crescimento: number
  obras_ativas: number
  obras_concluidas: number
  ticket_medio: number
}

export const gerarRelatorioDRE = async (dados: any[], periodo: string) => {
  const doc = new jsPDF()
  
  // Cabeçalho
  doc.setFontSize(20)
  doc.text('Demonstrativo de Resultado do Exercício (DRE)', 20, 30)
  
  doc.setFontSize(12)
  doc.text(`Período: ${periodo}`, 20, 45)
  doc.text(`Gerado em: ${new Date().toLocaleDateString('pt-BR')}`, 20, 55)
  
  // Calcular DRE
  const dre = calcularDRE(dados)
  
  // Tabela DRE
  const tableData = [
    ['RECEITAS', '', formatCurrency(dre.receitas.total)],
    ['  Receitas Operacionais', '', formatCurrency(dre.receitas.operacionais)],
    ['  Receitas Financeiras', '', formatCurrency(dre.receitas.financeiras)],
    ['  Outras Receitas', '', formatCurrency(dre.receitas.outras)],
    ['', '', ''],
    ['(-) CUSTOS DOS SERVIÇOS', '', formatCurrency(dre.custos.total)],
    ['  Materiais', '', formatCurrency(dre.custos.materiais)],
    ['  Mão de Obra', '', formatCurrency(dre.custos.maoObra)],
    ['  Equipamentos', '', formatCurrency(dre.custos.equipamentos)],
    ['', '', ''],
    ['= RESULTADO BRUTO', '', formatCurrency(dre.resultado.bruto)],
    ['', '', ''],
    ['(-) DESPESAS OPERACIONAIS', '', formatCurrency(dre.despesas.total)],
    ['  Despesas Administrativas', '', formatCurrency(dre.despesas.administrativas)],
    ['  Despesas Comerciais', '', formatCurrency(dre.despesas.comerciais)],
    ['  Despesas Financeiras', '', formatCurrency(dre.despesas.financeiras)],
    ['  Despesas Tributárias', '', formatCurrency(dre.despesas.tributarias)],
    ['', '', ''],
    ['= RESULTADO OPERACIONAL', '', formatCurrency(dre.resultado.operacional)],
    ['', '', ''],
    ['= RESULTADO LÍQUIDO', '', formatCurrency(dre.resultado.liquido)],
    ['', '', ''],
    ['MARGEM LÍQUIDA (%)', '', `${dre.resultado.margem.toFixed(2)}%`]
  ]
  
  doc.autoTable({
    head: [['Descrição', '', 'Valor (R$)']],
    body: tableData,
    startY: 70,
    styles: { fontSize: 10 },
    headStyles: { fillColor: [66, 139, 202] },
    columnStyles: {
      0: { cellWidth: 100 },
      1: { cellWidth: 20 },
      2: { cellWidth: 60, halign: 'right' }
    }
  })
  
  // Análise
  const finalY = (doc as any).lastAutoTable.finalY + 20
  doc.setFontSize(14)
  doc.text('Análise do Período', 20, finalY)
  
  doc.setFontSize(10)
  const analise = [
    `• Receita Total: ${formatCurrency(dre.receitas.total)}`,
    `• Custo Total: ${formatCurrency(dre.custos.total)}`,
    `• Margem Bruta: ${((dre.resultado.bruto / dre.receitas.total) * 100).toFixed(2)}%`,
    `• Margem Líquida: ${dre.resultado.margem.toFixed(2)}%`,
    `• Resultado: ${dre.resultado.liquido >= 0 ? 'LUCRO' : 'PREJUÍZO'} de ${formatCurrency(Math.abs(dre.resultado.liquido))}`
  ]
  
  analise.forEach((linha, index) => {
    doc.text(linha, 20, finalY + 15 + (index * 8))
  })
  
  return doc
}

export const gerarRelatorioMargemObra = async (dados: any[], obras: any[]) => {
  const doc = new jsPDF()
  
  // Cabeçalho
  doc.setFontSize(20)
  doc.text('Relatório de Margem por Obra', 20, 30)
  
  doc.setFontSize(12)
  doc.text(`Gerado em: ${new Date().toLocaleDateString('pt-BR')}`, 20, 45)
  
  // Calcular margens por obra
  const margensObra = calcularMargemPorObra(dados, obras)
  
  // Tabela de margens
  const tableData = margensObra.map(obra => [
    obra.nome_obra,
    obra.cliente,
    formatCurrency(obra.valor_contrato),
    formatCurrency(obra.custos_realizados),
    formatCurrency(obra.receitas_realizadas),
    `${obra.margem_bruta.toFixed(2)}%`,
    `${obra.margem_liquida.toFixed(2)}%`,
    `${obra.percentual_execucao.toFixed(1)}%`,
    obra.status
  ])
  
  doc.autoTable({
    head: [['Obra', 'Cliente', 'Valor Contrato', 'Custos', 'Receitas', 'Margem Bruta', 'Margem Líquida', '% Execução', 'Status']],
    body: tableData,
    startY: 60,
    styles: { fontSize: 8 },
    headStyles: { fillColor: [66, 139, 202] },
    columnStyles: {
      2: { halign: 'right' },
      3: { halign: 'right' },
      4: { halign: 'right' },
      5: { halign: 'center' },
      6: { halign: 'center' },
      7: { halign: 'center' }
    }
  })
  
  // Resumo
  const finalY = (doc as any).lastAutoTable.finalY + 20
  doc.setFontSize(14)
  doc.text('Resumo Executivo', 20, finalY)
  
  const totalContratos = margensObra.reduce((sum, obra) => sum + obra.valor_contrato, 0)
  const totalCustos = margensObra.reduce((sum, obra) => sum + obra.custos_realizados, 0)
  const totalReceitas = margensObra.reduce((sum, obra) => sum + obra.receitas_realizadas, 0)
  const margemGeralBruta = ((totalReceitas - totalCustos) / totalReceitas) * 100
  
  doc.setFontSize(10)
  const resumo = [
    `• Total de Obras: ${margensObra.length}`,
    `• Valor Total dos Contratos: ${formatCurrency(totalContratos)}`,
    `• Total de Custos Realizados: ${formatCurrency(totalCustos)}`,
    `• Total de Receitas Realizadas: ${formatCurrency(totalReceitas)}`,
    `• Margem Bruta Geral: ${margemGeralBruta.toFixed(2)}%`,
    `• Obras em Andamento: ${margensObra.filter(o => o.status === 'em_andamento').length}`,
    `• Obras Concluídas: ${margensObra.filter(o => o.status === 'concluida').length}`
  ]
  
  resumo.forEach((linha, index) => {
    doc.text(linha, 20, finalY + 15 + (index * 8))
  })
  
  return doc
}

export const gerarRelatorioPerformance = async (dados: any[], periodo: string) => {
  const doc = new jsPDF()
  
  // Cabeçalho
  doc.setFontSize(20)
  doc.text('Relatório de Performance Mensal', 20, 30)
  
  doc.setFontSize(12)
  doc.text(`Período: ${periodo}`, 20, 45)
  doc.text(`Gerado em: ${new Date().toLocaleDateString('pt-BR')}`, 20, 55)
  
  // Calcular performance
  const performance = calcularPerformanceMensal(dados)
  
  // Tabela de performance
  const tableData = performance.map(mes => [
    mes.mes,
    formatCurrency(mes.receitas),
    formatCurrency(mes.despesas),
    `${mes.margem.toFixed(2)}%`,
    `${mes.crescimento >= 0 ? '+' : ''}${mes.crescimento.toFixed(2)}%`,
    mes.obras_ativas.toString(),
    mes.obras_concluidas.toString(),
    formatCurrency(mes.ticket_medio)
  ])
  
  doc.autoTable({
    head: [['Mês', 'Receitas', 'Despesas', 'Margem', 'Crescimento', 'Obras Ativas', 'Obras Concluídas', 'Ticket Médio']],
    body: tableData,
    startY: 70,
    styles: { fontSize: 9 },
    headStyles: { fillColor: [66, 139, 202] },
    columnStyles: {
      1: { halign: 'right' },
      2: { halign: 'right' },
      3: { halign: 'center' },
      4: { halign: 'center' },
      5: { halign: 'center' },
      6: { halign: 'center' },
      7: { halign: 'right' }
    }
  })
  
  // Indicadores
  const finalY = (doc as any).lastAutoTable.finalY + 20
  doc.setFontSize(14)
  doc.text('Indicadores de Performance', 20, finalY)
  
  const receitaTotal = performance.reduce((sum, mes) => sum + mes.receitas, 0)
  const despesaTotal = performance.reduce((sum, mes) => sum + mes.despesas, 0)
  const margemMedia = performance.reduce((sum, mes) => sum + mes.margem, 0) / performance.length
  const crescimentoMedio = performance.reduce((sum, mes) => sum + mes.crescimento, 0) / performance.length
  
  doc.setFontSize(10)
  const indicadores = [
    `• Receita Total do Período: ${formatCurrency(receitaTotal)}`,
    `• Despesa Total do Período: ${formatCurrency(despesaTotal)}`,
    `• Resultado do Período: ${formatCurrency(receitaTotal - despesaTotal)}`,
    `• Margem Média: ${margemMedia.toFixed(2)}%`,
    `• Crescimento Médio: ${crescimentoMedio.toFixed(2)}%`,
    `• Melhor Mês: ${performance.reduce((max, mes) => mes.margem > max.margem ? mes : max).mes}`,
    `• Maior Receita: ${formatCurrency(Math.max(...performance.map(m => m.receitas)))}`
  ]
  
  indicadores.forEach((linha, index) => {
    doc.text(linha, 20, finalY + 15 + (index * 8))
  })
  
  return doc
}

const calcularDRE = (dados: any[]): DREData => {
  const receitas = {
    operacionais: dados.filter(d => d.categoria === 'recebimento').reduce((sum, d) => sum + d.valor, 0),
    financeiras: dados.filter(d => d.categoria === 'financeiro' && d.tipo === 'entrada').reduce((sum, d) => sum + d.valor, 0),
    outras: dados.filter(d => d.categoria === 'outros' && d.tipo === 'entrada').reduce((sum, d) => sum + d.valor, 0),
    total: 0
  }
  receitas.total = receitas.operacionais + receitas.financeiras + receitas.outras
  
  const custos = {
    materiais: dados.filter(d => d.categoria === 'material').reduce((sum, d) => sum + d.valor, 0),
    maoObra: dados.filter(d => d.categoria === 'mao_obra').reduce((sum, d) => sum + d.valor, 0),
    equipamentos: dados.filter(d => d.categoria === 'equipamentos').reduce((sum, d) => sum + d.valor, 0),
    total: 0
  }
  custos.total = custos.materiais + custos.maoObra + custos.equipamentos
  
  const despesas = {
    administrativas: dados.filter(d => d.categoria === 'administrativo').reduce((sum, d) => sum + d.valor, 0),
    comerciais: dados.filter(d => d.categoria === 'comercial').reduce((sum, d) => sum + d.valor, 0),
    financeiras: dados.filter(d => d.categoria === 'financeiro' && d.tipo === 'saida').reduce((sum, d) => sum + d.valor, 0),
    tributarias: dados.filter(d => d.categoria === 'tributario').reduce((sum, d) => sum + d.valor, 0),
    total: 0
  }
  despesas.total = despesas.administrativas + despesas.comerciais + despesas.financeiras + despesas.tributarias
  
  const resultado = {
    bruto: receitas.total - custos.total,
    operacional: receitas.total - custos.total - despesas.total,
    liquido: receitas.total - custos.total - despesas.total,
    margem: receitas.total > 0 ? ((receitas.total - custos.total - despesas.total) / receitas.total) * 100 : 0
  }
  
  return {
    periodo: '',
    receitas,
    custos,
    despesas,
    resultado
  }
}

const calcularMargemPorObra = (movimentacoes: any[], obras: any[]): MargemObraData[] => {
  return obras.map(obra => {
    const movimentacoesObra = movimentacoes.filter(m => m.obra_id === obra._id)
    
    const custos_realizados = movimentacoesObra
      .filter(m => m.tipo === 'saida')
      .reduce((sum, m) => sum + m.valor, 0)
    
    const receitas_realizadas = movimentacoesObra
      .filter(m => m.tipo === 'entrada')
      .reduce((sum, m) => sum + m.valor, 0)
    
    const valor_contrato = obra.valor_total || 0
    const margem_bruta = receitas_realizadas > 0 ? ((receitas_realizadas - custos_realizados) / receitas_realizadas) * 100 : 0
    const margem_liquida = valor_contrato > 0 ? ((receitas_realizadas - custos_realizados) / valor_contrato) * 100 : 0
    const percentual_execucao = valor_contrato > 0 ? (receitas_realizadas / valor_contrato) * 100 : 0
    
    return {
      obra_id: obra._id,
      nome_obra: obra.nome_projeto || 'N/A',
      cliente: obra.cliente || 'N/A',
      valor_contrato,
      custos_realizados,
      receitas_realizadas,
      margem_bruta,
      margem_liquida,
      percentual_execucao,
      status: obra.status || 'em_andamento'
    }
  })
}

const calcularPerformanceMensal = (dados: any[]): PerformanceData[] => {
  const mesesMap = new Map<string, any>()
  
  dados.forEach(movimento => {
    const data = new Date(movimento.data)
    const mesAno = `${data.getMonth() + 1}/${data.getFullYear()}`
    
    if (!mesesMap.has(mesAno)) {
      mesesMap.set(mesAno, {
        mes: mesAno,
        receitas: 0,
        despesas: 0,
        obras_ativas: new Set(),
        obras_concluidas: 0,
        valores_contratos: []
      })
    }
    
    const mesData = mesesMap.get(mesAno)
    
    if (movimento.tipo === 'entrada') {
      mesData.receitas += movimento.valor
    } else {
      mesData.despesas += movimento.valor
    }
    
    if (movimento.obra_id) {
      mesData.obras_ativas.add(movimento.obra_id)
    }
  })
  
  const meses = Array.from(mesesMap.values()).map((mes, index, array) => {
    const margem = mes.receitas > 0 ? ((mes.receitas - mes.despesas) / mes.receitas) * 100 : 0
    const crescimento = index > 0 ? ((mes.receitas - array[index - 1].receitas) / array[index - 1].receitas) * 100 : 0
    const ticket_medio = mes.obras_ativas.size > 0 ? mes.receitas / mes.obras_ativas.size : 0
    
    return {
      mes: mes.mes,
      receitas: mes.receitas,
      despesas: mes.despesas,
      margem,
      crescimento,
      obras_ativas: mes.obras_ativas.size,
      obras_concluidas: mes.obras_concluidas,
      ticket_medio
    }
  })
  
  return meses.sort((a, b) => {
    const [mesA, anoA] = a.mes.split('/').map(Number)
    const [mesB, anoB] = b.mes.split('/').map(Number)
    return new Date(anoA, mesA - 1).getTime() - new Date(anoB, mesB - 1).getTime()
  })
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value)
}

export const downloadPDF = (doc: jsPDF, filename: string) => {
  doc.save(filename)
}
